import { Loading } from '@/src/common/components/Loading'
import {
  CREATE_DOCUMENT_UPLOAD_SECURE_LINK,
  END_DOCUMENT_UPLOAD_SESSION,
} from '@/src/common/gql/mutations/highnote'
import { GENERATE_DOCUMENT_UPLOAD_CLIENT_TOKEN } from '@/src/common/gql/queries/card'
import { GET_DOCUMENT_UPLOAD_SESSION } from '@/src/common/gql/queries/highnote'
import { useLogError } from '@/src/common/hooks/useLogError'
import { HighnoteClient } from '@/src/services/Highnote'
import { useMutation, useQuery } from '@apollo/client'
import { useLoggedInQuery } from '@/src/common/gql/MesaGql'
import { getDocumentAsync } from 'expo-document-picker'
import { type ComponentProps, Fragment, useRef, useState, useEffect } from 'react'
import { DocumentType, DocumentUploadSessionStatusCode } from '@api/highnote/graphql'
import { ChevronWithIcon } from '@/src/common/components/ChevronWithIcon'
import { PassportIcon } from '@/src/assets/icons/PassportIcon'
import { startCase } from 'lodash'
import { TrashIcon } from '@/src/assets/icons/trash'
import AntDesign from '@expo/vector-icons/AntDesign'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { View, ScrollView, Platform, Text } from 'react-native'
import { Button } from '@/src/common/components/Button'
import { router } from 'expo-router'
import { Routes } from '@/src/common/enums/routes'
import { Spinner } from 'tamagui'
import BottomSheet from '@gorhom/bottom-sheet'
import { DesignSystem } from '@/src/design-system'
import { useSardine } from '@/src/services/Sardine'
import { CardApplicationWrapper } from './cardApplicationWrapper'
import { type ExpoDocumentPickerFile, uploadFile } from '@/src/common/utils/uploadFile'
import { GET_CARD_APPLICATION_STATUS } from '@/src/experiences/card-application/gql/queries'
import { mesaColorNames, typography } from '@/src/common/theme'
import { DEFAULT_FILE_TYPES } from '@/src/design-system/modal/UploadModal'
import { cardApplicationStyles } from './common'

const DocumentMap = Map<DocumentType, null | { file: ExpoDocumentPickerFile; uploading?: true }>

type ChevronWithIconProps = ComponentProps<typeof ChevronWithIcon>

interface DocumentRowProps
  extends Partial<Omit<ChevronWithIconProps, 'text' | 'caption'>>,
    Pick<ChevronWithIconProps, 'caption'> {
  documentType: DocumentType
}

function DocumentRow(props: DocumentRowProps) {
  return (
    <ChevronWithIcon
      segmentAction="User Pressed the Document Upload Item"
      Icon={<PassportIcon />}
      text={startCase(props.documentType.toLowerCase())}
      {...props}
    />
  )
}

export function DocumentUpload() {
  const Alert = useAlert(DocumentUpload.name)
  const documentUploadModalRef = useRef<BottomSheet>(null)
  const onError = useLogError()
  const { data: cardProductApplicationStatus } = useLoggedInQuery(GET_CARD_APPLICATION_STATUS, {
    fetchPolicy: 'network-only',
    pollInterval: 8888,
  })

  const { data: sessionClient } = useLoggedInQuery(GENERATE_DOCUMENT_UPLOAD_CLIENT_TOKEN, {
    skip: cardProductApplicationStatus?.getCardProductApplicationStatus.status !== 'IN_REVIEW',
    onError,
    fetchPolicy: 'network-only',
    pollInterval: 1000 * 60 * 4, // Regenerating every 4 minutes, Token expires after 5 minutes.
  })

  const { result } = sessionClient?.generateDocumentUploadClientToken || {}
  const clientToken =
    result?.__typename === 'HighnoteDocumentUploadClientTokenSuccess'
      ? result.clientTokenValue
      : undefined
  const sessionId =
    result?.__typename === 'HighnoteDocumentUploadClientTokenSuccess'
      ? result.documentUploadSessionId
      : undefined
  const shouldRedirectToApplicationInReview =
    result?.__typename === 'HighnoteDocumentUploadClientTokenUnavailable'

  // picked = selected in the app to be uploaded
  // uploaded = uploaded to highnote for review
  const [pickableDocuments, setPickableDocuments] = useState(() => new DocumentMap())

  const [endDocumentUploadSession] = useMutation(END_DOCUMENT_UPLOAD_SESSION, {
    client: HighnoteClient,
    context: { clientToken },
    refetchQueries: [GET_DOCUMENT_UPLOAD_SESSION, GET_CARD_APPLICATION_STATUS],
  })

  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const { data: sessionPayload } = useQuery(GET_DOCUMENT_UPLOAD_SESSION, {
    skip: !clientToken || !sessionId,
    fetchPolicy: 'network-only',
    client: HighnoteClient,
    context: clientToken ? { clientToken } : undefined,
    onError,
    variables: sessionId ? { sessionId } : undefined,
    pollInterval: 2222, // polling isn't pretty but the uploading of the files isn't an endpoint that we can model reactively/synchronously with highnote's graphql api + apollo
    notifyOnNetworkStatusChange: true, // this allows `onCompleted` to be called whenever `pollInterval` polls
    onCompleted({ node }) {
      if (node?.__typename !== 'USAccountHolderApplicationDocumentUploadSession') return

      if (node.status === DocumentUploadSessionStatusCode.Submitted) {
        router.replace(Routes.ApplicationInReview.route)
        return
      }

      const newRequiredDocumentTypes = node.primaryDocumentTypes

      const totalRequiredDocumentTypes = newRequiredDocumentTypes
        ? new DocumentMap([
            ...pickableDocuments,
            ...newRequiredDocumentTypes.map(
              (documentType) =>
                [documentType, pickableDocuments.get(documentType) ?? null] as const,
            ),
          ])
        : pickableDocuments

      setPickableDocuments(totalRequiredDocumentTypes)

      if (!sessionId) return

      const countOfUploadedDocuments = node.documents?.length || 0
      const countOfPickableDocuments = pickableDocuments.size

      if (countOfPickableDocuments > countOfUploadedDocuments) return

      // for some reason, the highnote api doesn't return the same # of uploadRequirements.constraints.numberOfDocuments between
      // status: INITIATED (in which it's implied to be non-0) and
      // status: IN_PROGRESS (in which highnote makes it 0, at least in the test environment)
      //
      // while we can "remember" the original # documents that were originally required from the INITIATED stage using some
      // clever _stateful_ approach, like useState, AsyncStorage, our database, etc., there can always be a situation where,
      // if this is truly how highnote behaves, we just cannot recover the original # of documents required, e.g. resuming
      // the application on a different device and/or intermittent network issues that cause the data to fail to be persisted
      // anywhere (like our database). as such, i say it'll be easier if we just stick with the things that we already get for
      // free from the base user experience: the same-session cache from the `pickedDocuments` `useState`. that is, since we
      // know that `pickedDocuments` can _only_ ever be populated by the user specifically reacting to the original INITIATED
      // stage's # of required documents, we can safely assume that any documents.length < pickedDocuments.size is an unfulfilled
      // document upload session. on the other hand, in the cases where `pickedDocuments` is not available (e.g. due to the
      // aforementioned causes), we'll just end the document upload session then and there (which is mostly the situation this
      // block of code is in). i think it's ok to end the document upload session even if the original # of required documents
      // were not all uploaded because, since this is a manual review process, it is recoverable simply by the review approving
      // or re-requesting the documents.
      void endDocumentUploadSession({
        variables: { input: { documentUploadSessionId: sessionId } },
        refetchQueries: [GET_CARD_APPLICATION_STATUS, GET_DOCUMENT_UPLOAD_SESSION],
      })
    },
  })

  const session =
    sessionPayload?.node?.__typename === 'USAccountHolderApplicationDocumentUploadSession'
      ? sessionPayload.node
      : undefined

  const requiredDocumentTypes = session?.primaryDocumentTypes

  const uploadedDocuments = session?.documents

  const uploadedDocumentTypes = uploadedDocuments?.flatMap((document) =>
    document.__typename === 'DocumentUploadLink' && document.documentType
      ? [document.documentType]
      : [],
  )

  /** unique and sorted */
  const encounteredDocumentTypes = Array.from(
    new Set([
      ...(requiredDocumentTypes || []),
      ...Array.from(pickableDocuments.keys()),
      ...(uploadedDocumentTypes || []),
    ]),
  ).toSorted()

  const [generateUploadLink] = useMutation(CREATE_DOCUMENT_UPLOAD_SECURE_LINK, {
    client: HighnoteClient,
    context: { clientToken },
  })

  const { updateOptions } = useSardine()

  useEffect(() => {
    void updateOptions({ flow: 'application-document-upload' })
  }, [updateOptions])

  function handlePickedFile(file: ExpoDocumentPickerFile, documentType?: DocumentType) {
    documentType &&
      setPickableDocuments((prev) => new DocumentMap([...prev, [documentType, { file }]]))
  }

  const [selectedDocumentType, setSelectedDocumentType] = useState<DocumentType>()
  function handleOpenPicker(documentType: DocumentType) {
    setSelectedDocumentType(documentType)

    if (Platform.OS === 'web') {
      getDocumentAsync({ type: DEFAULT_FILE_TYPES, copyToCacheDirectory: true, multiple: false })
        .then(({ assets, output }) => {
          const asset = assets?.[0]
          if (!asset) return

          const { uri, mimeType, name } = asset

          if (uri && mimeType)
            handlePickedFile({ uri, mimeType, name, output: output ?? undefined }, documentType)
          else Alert.alert(`${name} is not a valid document`)
        })
        .catch(onError)
    } else {
      documentUploadModalRef.current?.snapToIndex(0)
    }
  }

  function renderRequiredDocument(documentType: DocumentType) {
    const uploadedDocument = uploadedDocuments
      ?.values()
      .flatMap((document) =>
        document.__typename === 'DocumentUploadLink' && document.documentType
          ? [document.documentType]
          : [],
      )
      .find((d) => d === documentType)

    if (uploadedDocument) {
      return (
        <DocumentRow
          documentType={uploadedDocument}
          caption="Document uploaded"
          disabled
          IconEnd={
            <AntDesign
              name="checkcircle"
              size={24}
              color={mesaColorNames['semantic/fg/success #008557']}
              caption="Document uploaded"
            />
          }
        />
      )
    }

    const { file, uploading } = pickableDocuments.get(documentType) || {}

    return (
      <DocumentRow
        documentType={documentType}
        caption={file?.name ?? 'No file selected'}
        onPress={() => handleOpenPicker(documentType)}
        IconEnd={
          uploading ? (
            <Spinner color={mesaColorNames['semantic/fg/subtle #898D8F']} scale={0.8} />
          ) : file ? (
            <TrashIcon pathProps={{ stroke: mesaColorNames['semantic/fg/error #DB340B'] }} />
          ) : (
            <AntDesign
              name="pluscircleo"
              size={24}
              color={mesaColorNames['Mesa - Brand/Secondary #223843']}
            />
          )
        }
        onIconEndPress={() => {
          if (file) {
            setPickableDocuments((prev) => new DocumentMap([...prev, [documentType, null]]))
          } else {
            handleOpenPicker(documentType)
          }
        }}
      />
    )
  }

  const pickedButNotUploaded = pickableDocuments
    .entries()
    .flatMap(([documentType, file]) => (file ? ([[documentType, file]] as const) : []))
    .filter(([documentType]) => !uploadedDocumentTypes?.includes(documentType))
    .toArray()

  const submittable = pickedButNotUploaded.length && sessionId

  const [uploading, setUploading] = useState(false)

  async function uploadDocuments() {
    try {
      if (!submittable) return
      setUploading(true)

      /** upload the files sequentially to avoid highnote api rate limiting */
      const results = await Array.fromAsync(
        (async function* () {
          for (const [documentType, { file }] of pickedButNotUploaded) {
            const { data, errors } = await generateUploadLink({
              variables: { input: { documentType, documentUploadSessionId: sessionId } },
            })

            switch (data?.createDocumentUploadLink?.__typename) {
              case 'UserError':
                yield data.createDocumentUploadLink
                continue
              case 'DocumentUploadLink':
                if (!data.createDocumentUploadLink.uploadUrl)
                  throw new Error('no uploadUrl', {
                    cause: { data, errors, documentType, file },
                  })
                break
              default:
                throw new Error('failed to generate upload link', {
                  cause: { data, errors, documentType, file },
                })
            }

            await uploadFile(file, data.createDocumentUploadLink.uploadUrl)

            // ok we've done what we can to upload the files from our side (FileSystem.uploadAsync).
            // now it's highnote's turn to do its thing and report to us that it was successfully
            // uploaded at some point through our GET_DOCUMENT_UPLOAD_SESSION polling. in the meantime,
            // indicate to the user that the file is being processed with this `uploading: true`
            setPickableDocuments(
              (prev) => new DocumentMap([...prev, [documentType, { file, uploading: true }]]),
            )

            yield documentType
          }
        })(),
      )

      results
        .values()
        .filter((value) => typeof value !== 'string')
        .flatMap((value) => value.errors ?? [])
        .map((error) => error.description)
        .filter((description) => typeof description === 'string')
        .take(1)
        .forEach((userError) => Alert.alert("Documents couldn't be uploaded", userError))
    } catch (error) {
      onError(error)
      Alert.alert('Failed to upload documents', 'Please try again')
    } finally {
      setUploading(false)
    }
  }

  if (shouldRedirectToApplicationInReview) {
    router.replace(Routes.ApplicationInReview.route)
    return null
  }

  return (
    <CardApplicationWrapper>
      <ScrollView>
        <View style={cardApplicationStyles.headerText}>
          <Text style={typography['heading/heading2']}>You're almost done</Text>
        </View>
        <View style={cardApplicationStyles.instructionText}>
          <Text>
            Additional document verification is required. Please provide the below requested
            documentation to proceed with review of your application. Once all requested
            documentation has been uploaded, tap Submit Documents to continue with your application.
          </Text>
        </View>
        {session ? (
          encounteredDocumentTypes.map((documentType) => (
            <Fragment key={documentType}>{renderRequiredDocument(documentType)}</Fragment>
          ))
        ) : (
          <Loading />
        )}
      </ScrollView>
      <View style={{ marginBottom: 0 }}>
        {submittable ? (
          <Button
            title={`Upload Document${pickedButNotUploaded.length > 1 ? 's' : ''}`}
            disabled={!submittable || uploading}
            loading={uploading}
            onPress={() => void uploadDocuments()}
          />
        ) : null}
        <Button
          buttonStyle={cardApplicationStyles.returnHomeButtonStyles}
          titleStyle={cardApplicationStyles.returnHomeTitleStyles}
          title="Return Home"
          onPress={() => router.replace(Routes.Main.route)}
        />
      </View>
      <DesignSystem.Modal.Upload
        title="Identity Verification"
        ref={documentUploadModalRef}
        onUploadPhoto={(file) => handlePickedFile(file, selectedDocumentType)}
        onUploadLibrary={(file) => handlePickedFile(file, selectedDocumentType)}
        onUploadFile={(file) => handlePickedFile(file, selectedDocumentType)}
      />
    </CardApplicationWrapper>
  )
}
